/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { bindActionCreators } from "redux";
import { toast, ToastContainer } from "react-toastify";
import moment from "moment";
import { ArrowLeft, Send, User as UserIcon, Ticket, Pencil } from "lucide-react";
import RenderSupportPage from "../RenderSupportPage";
import PageSkeleton from "../../Components/PageSkeleton";
import { messageActionCreators, authActionCreators } from "../../services/Actions";

// Conversation view + reply box for a member's message to Customer Service.
// Posts through the shared /messages/add-by-system-user endpoint, so a reply
// lands in the member's app exactly like a loans or mart officer reply.

function SupportMessageDetailed() {
  const dispatch = useDispatch();
  const history = useHistory();
  const scrollRef = useRef();

  const chat_id = history.location?.state?.chat_id;
  const routeUserData = history.location?.state?.userData;
  const routeReference = history.location?.state?.reference;
  const routeSubject = history.location?.state?.subject;

  const {
    getMessageDetailed,
    addMessageBySystemUser,
    EditMessageBySystemUser,
    ResetMessagesResponse,
  } = bindActionCreators(messageActionCreators, dispatch);
  const { GetUserProfile } = bindActionCreators(authActionCreators, dispatch);

  const auth = useSelector((state) => state?.auth);
  const messages = useSelector((state) => state?.message);
  const { messageDetailed, response, isLoading } = messages;

  const [body, setBody] = useState("");
  const [hasLoaded, setHasLoaded] = useState(false);

  // How long an agent may edit a reply after sending it. 15 minutes matches
  // WhatsApp, so the affordance is already familiar, and it is short enough
  // that the member is unlikely to have acted on the original — these replies
  // carry loan and settlement figures, so a late edit would rewrite what the
  // member was told with no trace.
  const EDIT_WINDOW_MS = 15 * 60 * 1000;

  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState("");
  // Ticks so the Edit button disappears on its own when the window closes,
  // rather than staying until the next re-render.
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(t);
  }, []);

  // Only the desk's own replies, and only inside the window. `m.data` is the
  // send timestamp the backend writes on each chat entry.
  const editableFor = (m) => {
    if (!m?.data || !m?._id) return 0;
    const sentAt = new Date(m.data).getTime();
    if (Number.isNaN(sentAt)) return 0;
    return Math.max(0, EDIT_WINDOW_MS - (now - sentAt));
  };

  const startEdit = (m) => {
    setEditingId(m._id);
    setEditBody(m.message || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditBody("");
  };

  const saveEdit = (m) => {
    const text = editBody.trim();
    if (!text) {
      toast.error("Message cannot be empty");
      return;
    }
    if (editableFor(m) <= 0) {
      toast.error("The edit window for this message has closed");
      cancelEdit();
      return;
    }
    Promise.resolve(
      EditMessageBySystemUser(
        {
          message: text,
          system_user: auth?.user?._id,
          system_user_name: auth?.user?.name,
          system_user_profile: auth?.user?.profile_image,
          reference: messageDetailed?.reference || routeReference,
        },
        m._id
      )
    ).then(() => chat_id && getMessageDetailed(chat_id));
    cancelEdit();
  };

  const member = messageDetailed?.userData || routeUserData;
  const chat = messageDetailed?.chat || [];

  useEffect(() => {
    GetUserProfile();
  }, []);

  useEffect(() => {
    if (!chat_id) {
      setHasLoaded(true);
      return;
    }
    Promise.resolve(getMessageDetailed(chat_id)).finally(() => setHasLoaded(true));
  }, [chat_id]);

  // The server emits message:new to the conversation room after a reply is
  // saved, so the thread updates without a re-fetch.
  useEffect(() => {
    if (!chat_id) return;
    const socket = io("https://api.upnmg.com", {
      transports: ["websocket", "polling"],
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
    });
    // The gateway keys these rooms on `conversationId` and silently ignores any
    // other payload shape — do not rename this field.
    const join = () => socket.emit("conversation:join", { conversationId: chat_id });
    socket.on("connect", join);
    socket.on("reconnect", join);
    socket.on("message:new", (msg) => {
      dispatch({ type: "APPEND_CHAT_MESSAGE", payload: msg });
    });
    return () => {
      socket.emit("conversation:leave", { conversationId: chat_id });
      socket.disconnect();
    };
  }, [chat_id]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat]);

  useEffect(() => {
    if (response?.state === "ERROR") {
      toast.error(response?.message ?? "Unable to send the reply");
      setTimeout(() => ResetMessagesResponse(), 1500);
    } else if (response?.state === "SUCCESS") {
      setTimeout(() => ResetMessagesResponse(), 1500);
    }
  }, [response?.state]);

  const handleSend = (e) => {
    e?.preventDefault();
    if (!body.trim()) {
      toast.error("Type a reply first");
      return;
    }
    // Re-fetch the thread once the POST resolves. The socket `message:new`
    // push is the fast path, but it can't be relied on alone — if the room
    // join hasn't landed (or the gateway isn't reachable) the reply would only
    // show after a manual page refresh. GET_MESSAGE_DETAILED replaces the
    // thread wholesale and APPEND_CHAT_MESSAGE dedups on `_id`, so the two
    // paths can't produce a duplicate bubble.
    Promise.resolve(
      addMessageBySystemUser({
        message: body.trim(),
        system_user: auth?.user?._id,
        system_user_name: auth?.user?.name,
        system_user_profile: auth?.user?.profile_image,
        reference: messageDetailed?.reference || routeReference,
      })
    ).then(() => chat_id && getMessageDetailed(chat_id));
    setBody("");
  };

  // Enter sends, Shift+Enter (or Ctrl/Cmd+Enter) inserts a newline. `isComposing`
  // guards IME input, where Enter commits the candidate rather than the message.
  const handleKeyDown = (e) => {
    if (e.key !== "Enter") return;
    if (e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.nativeEvent?.isComposing) return;
    e.preventDefault();
    handleSend();
  };

  return (
    <RenderSupportPage title={routeSubject || "Conversation"}>
      <ToastContainer />

      <div className="flex items-center justify-between -mt-4 mb-4">
        <button
          onClick={() => history.push("/support/messages")}
          className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400"
        >
          <ArrowLeft size={14} /> Back to messages
        </button>
        {member?.staff_id && (
          <div className="flex items-center gap-2">
            <button
              onClick={() =>
                history.push(`/support/member-detailed?staff_id=${member.staff_id}`)
              }
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border border-slate-200 dark:border-gray-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-700"
            >
              <UserIcon size={13} /> Member profile
            </button>
            <button
              onClick={() => history.push("/support/tickets")}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm"
            >
              <Ticket size={13} /> Raise ticket
            </button>
          </div>
        )}
      </div>

      {!hasLoaded && chat.length === 0 ? (
        <PageSkeleton cards={0} rows={6} cols={3} />
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 flex flex-col">
          {/* Member header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 dark:border-gray-700">
            <img
              className="w-10 h-10 rounded-full object-cover border border-slate-200 dark:border-gray-600"
              src={member?.profile_image || "/images/dev/avarta2.jpeg"}
              alt="Member"
            />
            <div className="min-w-0">
              <p className="text-sm font-bold text-slate-800 dark:text-slate-100 truncate">
                {member?.name || "Member"}
              </p>
              <p className="text-[11px] text-slate-400 dark:text-slate-500">
                {member?.staff_id || "—"} · #
                {messageDetailed?.reference || routeReference || "—"}
              </p>
            </div>
          </div>

          {/* Thread */}
          <div className="flex-1 p-4 space-y-3 max-h-[520px] overflow-y-auto">
            {chat.length === 0 ? (
              <p className="text-xs text-slate-400 dark:text-slate-500 text-center py-12">
                No messages in this conversation yet.
              </p>
            ) : (
              chat.map((m, i) => {
                // Only a staff entry sits on the right. Test for `system_user`
                // rather than `member` so anything without a usable
                // `user_type` falls to the member side — an unlabelled entry
                // is the member's message, and showing it on the right would
                // read as though we sent it. Same test the loans thread uses.
                const fromMember = m?.user_type !== "system_user";
                // Member side prefers the avatar stored on the entry itself,
                // then the conversation's member. Staff side likewise uses the
                // entry's `system_user_profile` (saved at send time) so a
                // thread answered by two agents shows each one — falling back
                // to the signed-in user for older entries.
                const avatar = fromMember
                  ? m?.member_profile || member?.profile_image
                  : m?.system_user_profile || auth?.user?.profile_image;
                // Rendered before or after the bubble depending on the side.
                // Deliberately NOT done with `flex-row-reverse`: reversing the
                // main axis also flips what `justify-end` means, which pushes
                // the row back to the left.
                const avatarImg = (
                  <img
                    className="w-7 h-7 rounded-full object-cover border border-slate-200 dark:border-gray-600 flex-shrink-0"
                    src={avatar || "/images/dev/avarta2.jpeg"}
                    onError={(e) => {
                      e.currentTarget.src = "/images/dev/avarta2.jpeg";
                    }}
                    alt={fromMember ? "Member" : "Support agent"}
                  />
                );
                return (
                  <div
                    key={m?._id || i}
                    className={`flex items-end gap-2 ${fromMember ? "justify-start" : "justify-end"}`}
                  >
                    {fromMember && avatarImg}
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                        fromMember
                          ? "bg-slate-100 dark:bg-gray-700"
                          : "bg-indigo-600 text-white"
                      }`}
                    >
                      <p className={`text-[10px] font-bold uppercase tracking-wide mb-1 ${fromMember ? "text-slate-500 dark:text-slate-400" : "text-indigo-100"}`}>
                        {fromMember
                          ? member?.name || "Member"
                          : m?.system_user_name || m?.system_user || "Customer Service"}
                      </p>
                      {editingId === m?._id ? (
                        <div className="space-y-2">
                          <textarea
                            value={editBody}
                            onChange={(e) => setEditBody(e.target.value)}
                            rows={3}
                            autoFocus
                            className="w-full px-2 py-1.5 text-xs rounded-lg bg-white/95 text-slate-800 border border-white/40 focus:outline-none resize-none"
                          />
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={cancelEdit}
                              className="px-2.5 py-1 rounded-md text-[11px] font-bold bg-white/20 text-white hover:bg-white/30"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => saveEdit(m)}
                              className="px-2.5 py-1 rounded-md text-[11px] font-bold bg-white text-indigo-700 hover:bg-indigo-50"
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className={`text-xs whitespace-pre-wrap ${fromMember ? "text-slate-700 dark:text-slate-200" : "text-white"}`}>
                          {m?.message}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <p className={`text-[10px] ${fromMember ? "text-slate-400 dark:text-slate-500" : "text-indigo-200"}`}>
                          {m?.data && moment(m.data).isValid()
                            ? moment(m.data).format("MMM DD, HH:mm")
                            : ""}
                        </p>
                        {/* Edit is offered only on our own replies, and only
                            while the window is open — see EDIT_WINDOW_MS. */}
                        {!fromMember && editingId !== m?._id && editableFor(m) > 0 && (
                          <button
                            onClick={() => startEdit(m)}
                            title={`Editable for ${Math.ceil(editableFor(m) / 60000)} more min`}
                            className="flex items-center gap-1 text-[10px] font-bold text-indigo-200 hover:text-white"
                          >
                            <Pencil size={11} />
                            Edit · {Math.ceil(editableFor(m) / 60000)}m
                          </button>
                        )}
                      </div>
                    </div>
                    {!fromMember && avatarImg}
                  </div>
                );
              })
            )}
            <div ref={scrollRef} />
          </div>

          {/* Reply */}
          <form onSubmit={handleSend} className="p-3 border-t border-slate-100 dark:border-gray-700 flex items-end gap-2">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={2}
              placeholder="Type your reply to the member… (Enter to send, Shift+Enter for a new line)"
              className="flex-1 px-3 py-2 text-sm border border-slate-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-slate-700 dark:text-slate-200 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
            />
            <button
              type="submit"
              className="p-2.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm transition-colors"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </RenderSupportPage>
  );
}

export default SupportMessageDetailed;
